import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, DollarSign, Calendar, Activity, Bed, MessageSquare, Settings,
  LogOut, PieChart as PieChartIcon, BarChart3, Clock, ChevronLeft, ChevronRight,
  Eye, Inbox, Search, Filter, Building2, MapPin
} from 'lucide-react';
import './AdminHotelApprovals.css';

const STATUS_OPTIONS = [
  { value: 'all',         label: 'All status' },
  { value: 'active',      label: 'Active' },
  { value: 'inactive',    label: 'Inactive' },
  { value: 'maintenance', label: 'Maintenance' },
];

const statusPillVariant = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'active':      return 'positive';
    case 'inactive':    return 'pending';
    case 'maintenance': return 'negative';
    default:            return 'pending';
  }
};

function HotelManagement() {
  const { logout, currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!currentUser) throw new Error('Not authenticated');
      const idToken = await currentUser.getIdToken();
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search.trim()) params.set('search', search.trim());
      params.set('limit', '100');

      const res = await fetch(`/api/admin/hotels?${params.toString()}`, {
        headers: { Authorization: `Bearer ${idToken}` },
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to load hotels');
      setHotels(Array.isArray(data?.hotels) ? data.hotels : []);
    } catch (e) {
      setError(e.message || 'Failed to load hotels.');
      setHotels([]);
    }
    setLoading(false);
  }, [currentUser, statusFilter, search]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  const navItems = [
    { path: '/admin',               Icon: PieChartIcon,  label: 'Analytics' },
    { path: '/admin/approvals',     Icon: Clock,         label: 'Hotel approvals' },
    { path: '/admin/hotel',         Icon: Bed,           label: 'Hotel management' },
    { path: '/admin/users',         Icon: Users,         label: 'User management' },
    { path: '/admin/notifications', Icon: MessageSquare, label: 'Notifications' },
    { path: '/iot-management',      Icon: Activity,      label: 'IoT management' },
    { path: '/admin/bookings',      Icon: Calendar,      label: 'Bookings' },
    { path: '/admin/revenue',       Icon: DollarSign,    label: 'Revenue' },
    { path: '/admin/reports',       Icon: BarChart3,     label: 'Reports' },
    { path: '/admin/settings',      Icon: Settings,      label: 'Settings' },
  ];

  const isActive = (path) => path === '/admin'
    ? location.pathname === '/admin'
    : location.pathname.startsWith(path);

  const renderTable = () => (
    <div className="ib-card approvals-table-card">
      <table className="ib-table approvals-table">
        <thead>
          <tr>
            <th>Hotel</th>
            <th>Location</th>
            <th>Rooms</th>
            <th>Status</th>
            <th className="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {hotels.map(hotel => (
            <tr
              key={hotel.id}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/admin/hotel/${hotel.id}`)}
            >
              <td>
                <div className="cell-primary">
                  <Building2 size={14} style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--ib-text-3)' }} />
                  {hotel.name || 'Unnamed property'}
                </div>
                <div className="cell-secondary">ID #{hotel.id}</div>
              </td>
              <td>
                <div className="cell-primary" style={{ fontWeight: 500 }}>
                  <MapPin size={12} style={{ verticalAlign: '-1px', marginRight: 4, color: 'var(--ib-text-3)' }} />
                  {hotel.city || '—'}{hotel.country ? `, ${hotel.country}` : ''}
                </div>
                {hotel.address && <div className="cell-secondary">{hotel.address}</div>}
              </td>
              <td className="cell-muted">{hotel.total_rooms ?? 0}</td>
              <td>
                <span className={`ib-pill ib-pill--${statusPillVariant(hotel.status)}`}>
                  {hotel.status || 'unknown'}
                </span>
              </td>
              <td className="col-actions">
                <button
                  className="ib-btn ib-btn--ghost ib-btn--sm"
                  onClick={(e) => { e.stopPropagation(); navigate(`/admin/hotel/${hotel.id}`); }}
                >
                  <Eye size={14} /> View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="admin-dashboard admin-page">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">IB</span>
            {sidebarOpen && (
              <span className="brand-wordmark">
                InnBotics
                <span className="brand-eyebrow">Admin Console</span>
              </span>
            )}
          </div>
          <button
            className="toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Admin navigation">
          {navItems.map(({ path, Icon, label }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`nav-item ${isActive(path) ? 'active' : ''}`}
              title={!sidebarOpen ? label : undefined}
            >
              <span className="nav-icon"><Icon size={18} strokeWidth={1.75} /></span>
              {sidebarOpen && <span>{label}</span>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button onClick={async () => { await logout(); navigate('/login'); }} className="logout-btn">
            <span className="nav-icon"><LogOut size={18} strokeWidth={1.75} /></span>
            {sidebarOpen && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        <header className="ib-page-header">
          <div className="header-left">
            <p className="ib-eyebrow">Operations · Hotels</p>
            <h1 className="ib-display">Hotel management</h1>
            <p className="ib-lede">All properties on the InnBotics platform — review status and open a property to see its full profile.</p>
          </div>
          <div className="header-right">
            <div className="ib-tabs" role="tablist" aria-label="Status filter">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  role="tab"
                  className={statusFilter === opt.value ? 'active' : ''}
                  onClick={() => setStatusFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="ib-card ib-card--padded" style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Search size={16} style={{ color: 'var(--ib-text-3)' }} />
            <input
              type="text"
              placeholder="Search by hotel name or city…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                outline: 'none',
                fontSize: 14,
                color: 'var(--ib-text-1)',
              }}
            />
            <Filter size={14} style={{ color: 'var(--ib-text-3)' }} />
            <span style={{ fontSize: 12, color: 'var(--ib-text-3)' }}>
              {hotels.length} {hotels.length === 1 ? 'result' : 'results'}
            </span>
          </div>
        </div>

        {error && <div className="approval-error">{error}</div>}

        {loading ? (
          <div className="ib-card ib-card--padded">
            <div className="approval-loading">
              <div className="approval-spinner" aria-hidden="true" />
              <p>Loading hotels…</p>
            </div>
          </div>
        ) : hotels.length === 0 ? (
          <div className="ib-card ib-card--padded">
            <div className="ib-empty">
              <Inbox size={36} strokeWidth={1.5} color="#94A3B8" />
              <h3>No hotels found</h3>
              <p>
                {statusFilter === 'all' && !search.trim()
                  ? 'No properties have been onboarded yet.'
                  : 'Try adjusting the status filter or search term.'}
              </p>
            </div>
          </div>
        ) : (
          renderTable()
        )}
      </main>
    </div>
  );
}

export default HotelManagement;
